/*
 * cardRef (PROD-793) — what a saved profile card (insurance / photo ID) points at.
 *
 * A card reaches the profile as { data } (fresh capture), { url } (legacy: a data URL or a
 * link), { storagePath } (a storage key, e.g. cases/<case>/... copied in by the clinician
 * panel) or a bare key string. Only a data: or http(s) value can be handed to <img> as is;
 * a storage key must first be exchanged for a short-lived signed URL.
 * Pure on purpose: no imports, so scripts/check-card-ref-signed.mjs executes it.
 */
const isInline = (s) => typeof s === 'string' && (s.startsWith('data:') || /^https?:/i.test(s))

/** The storage key a card names, or null (a data:/http value is never a key). */
export function cardStorageKey(card) {
  if (!card) return null
  if (typeof card === 'string') return card && !isInline(card) ? card : null
  if (typeof card !== 'object') return null
  const k = card.storagePath || card.path
  if (typeof k === 'string' && k && !isInline(k)) return k
  if (typeof card.url === 'string' && card.url && !isInline(card.url)) return card.url
  return null
}

/** A src usable without signing (data: or http/https), or null. Never a bare key. */
export function cardInlineSrc(card) {
  if (!card) return null
  if (typeof card === 'string') return isInline(card) ? card : null
  if (typeof card.data === 'string' && card.data.startsWith('data:')) return card.data
  return isInline(card.url) ? card.url : null
}

/** True when the card holds anything to show (inline value or a storage key). */
export const hasCardRef = (card) => !!(cardInlineSrc(card) || cardStorageKey(card))

/**
 * Viewable src for one card. `sign(key)` exchanges a storage key for a signed URL (it may
 * resolve to the key itself or null on failure). Fresh capture first, then the key through
 * the signer, then an http url as a last resort. Null on failure: the caller shows its
 * placeholder, never an <img> pointing at a raw key. Signed URLs are not kept here.
 */
export async function resolveCardSrc(card, sign) {
  if (!card) return null
  if (typeof card === 'object' && typeof card.data === 'string' && card.data.startsWith('data:')) return card.data
  const key = cardStorageKey(card)
  if (key) {
    try {
      const signed = await sign(key)
      if (typeof signed === 'string' && signed && signed !== key && isInline(signed)) return signed
    } catch { /* fall through to the inline fallback */ }
  }
  return cardInlineSrc(card)
}

/**
 * s110 (TestFlight b77, Dr. Rami: "Images not rendering", the card thumbnails showed "?"): the storage key inside
 * an S3 presigned URL that has expired (a card saved with only its upload-time `url`), so it can be signed again.
 * Virtual-hosted (<bucket>.s3[.region].amazonaws.com/<key>) and path-style (s3[.region].amazonaws.com/<bucket>/<key>).
 * Null for anything that is not a presigned S3 URL.
 */
export function keyFromSignedUrl(url) {
  if (typeof url !== 'string' || !/^https:\/\//i.test(url) || !/[?&]X-Amz-(Signature|Credential)=/i.test(url)) return null
  let u
  try { u = new URL(url) } catch { return null }
  const host = u.hostname.toLowerCase()
  if (!/(^|\.)s3[.-]([a-z0-9-]+\.)?amazonaws\.com$|(^|\.)s3\.amazonaws\.com$/.test(host)) return null
  let path = u.pathname.replace(/^\/+/, '')
  if (/^s3[.-]/.test(host)) path = path.split('/').slice(1).join('/')   // path-style: drop the bucket
  try { path = decodeURIComponent(path) } catch { return null }
  return path || null
}
